import { TkticSelect } from "@/components/ui/TkticSelect";
/**
 * Schema-driven setting controls for Coach > Settings (M6b).
 *
 * Three control kinds, one per `TeamSettingSchemaEntry.kind`:
 *   - `SettingToggle`     → `bool`
 *   - `SettingChipGroup`  → `enum`        (pick exactly one)
 *   - `SettingChipMulti`  → `array_enum`  (pick a subset)
 *
 * The Settings page renders these FROM the server's `schema`
 * payload, so a backend schema change (new choice, new key) flows
 * through without a client edit. Each control takes a
 * `choices`/`options` list rather than hard-coding values.
 *
 * `disabled` reflects the read-only state (a plain coach can read
 * but not edit team settings) — disabled controls still render
 * their current value, they just can't be changed.
 *
 * No native checkboxes / radios — themed buttons throughout, per
 * the project's no-native-chrome rule.
 */

export type SettingToggleProps = {
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  "data-testid"?: string;
};

export function SettingToggle({
  label,
  description,
  checked,
  disabled,
  onChange,
  "data-testid": testid,
}: SettingToggleProps) {
  return (
    <div className="setting-toggle flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-display text-ink">{label}</div>
        {description ? (
          <p className="text-ink-muted text-xs mt-0.5">{description}</p>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        data-testid={testid}
        data-checked={checked}
        className={[
          "relative h-6 w-11 shrink-0 rounded-full border transition-colors",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          checked ? "bg-accent border-accent" : "bg-surface border-rule",
        ].join(" ")}
      >
        <span
          aria-hidden="true"
          className={[
            "absolute top-0.5 h-4 w-4 rounded-full transition-all",
            // ON: derived accent foreground on the accent track. OFF: an `ink`-colored
            // knob so it stays visible against the `bg-surface` track —
            // a fixed white knob was white-on-white in light mode, and a
            // fixed navy knob would be navy-on-navy in dark mode. `ink`
            // is navy in light mode and paper in dark mode, so it
            // contrasts with `surface` in both themes.
            checked ? "bg-accent-ink left-[1.5rem]" : "bg-ink left-0.5",
          ].join(" ")}
        />
      </button>
    </div>
  );
}

type ChoiceOption = { value: string; label: string };

export type SettingChipGroupProps = {
  label: string;
  description?: string;
  options: ReadonlyArray<ChoiceOption>;
  value: string;
  disabled?: boolean;
  onChange: (next: string) => void;
  "data-testid"?: string;
};

/** Single-select chip row — radio semantics, exactly one active. */
export function SettingChipGroup({
  label,
  description,
  options,
  value,
  disabled,
  onChange,
  "data-testid": testid,
}: SettingChipGroupProps) {
  return (
    <div data-testid={testid} className="setting-select">
      <div className="text-sm font-display text-ink">{label}</div>
      {description ? (
        <p className="text-ink-muted text-xs mt-0.5 mb-1.5">{description}</p>
      ) : null}
      <TkticSelect
        ariaLabel={label}
        options={[...options]}
        value={value}
        disabled={disabled}
        onChange={onChange}
      />
    </div>
  );
}

export type SettingChipMultiProps = {
  label: string;
  description?: string;
  options: ReadonlyArray<ChoiceOption>;
  /** The selected subset. */
  values: string[];
  disabled?: boolean;
  onChange: (next: string[]) => void;
  "data-testid"?: string;
};

/** Multi-select chip list — toggle a subset. */
export function SettingChipMulti({
  label,
  description,
  options,
  values,
  disabled,
  onChange,
  "data-testid": testid,
}: SettingChipMultiProps) {
  const selected = new Set(values);
  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    // Preserve the schema's `options` order in the emitted array so
    // two equivalent selections always serialize identically.
    onChange(options.filter((o) => next.has(o.value)).map((o) => o.value));
  };
  const grouped = testid === "setting-ai-allowed_draft_targets";
  const definitions = [
    {
      label: "Notes",
      values: [
        "player_summary",
        "what_happened",
        "why_it_matters",
        "what_to_do_next",
      ],
    },
    { label: "Clips", values: ["clip_title", "clip_description"] },
    { label: "Goals", values: ["goal_description", "goal_success_criteria"] },
    {
      label: "Recaps",
      values: [
        "summary_team_positives",
        "summary_team_improvements",
        "summary_training_focus",
      ],
    },
  ];
  const known = new Set(definitions.flatMap((g) => g.values));
  const groups = grouped
    ? [
        ...definitions.map((g) => ({
          label: g.label,
          options: options.filter((o) => g.values.includes(o.value)),
        })),
        { label: "Other", options: options.filter((o) => !known.has(o.value)) },
      ]
    : [{ label: "", options }];
  return (
    <div data-testid={testid} className="settings-targets">
      <div>
        <div className="text-sm text-ink">{label}</div>
        {description ? (
          <p className="mt-1 text-xs text-ink-muted">{description}</p>
        ) : null}
      </div>
      {groups
        .filter((g) => g.options.length > 0)
        .map((group) => (
          <div
            key={group.label}
            className={grouped ? "settings-target-group" : ""}
          >
            {group.label ? (
              <span className="settings-group-label">{group.label}</span>
            ) : null}
            <div
              role="group"
              aria-label={group.label ? `${label}: ${group.label}` : label}
              className="settings-choices"
            >
              {group.options.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  aria-pressed={selected.has(opt.value)}
                  disabled={disabled}
                  onClick={() => toggle(opt.value)}
                  data-value={opt.value}
                  data-active={selected.has(opt.value)}
                  className="settings-choice"
                >
                  <span aria-hidden="true" className="settings-choice-check" />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}
